import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { REQUEST } from '@nguniversal/express-engine/tokens';

@Injectable({
  providedIn: 'root',
})
export class CanonicalResolver implements Resolve<void> {
	constructor(
		@Inject(DOCUMENT) private doc: Document,
		@Inject(PLATFORM_ID) private platformId: Object,
		@Optional() @Inject(REQUEST) private request: any,
	) {}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): void {
		// Must never throw: this resolver is attached to every route, so an
		// exception here cancels navigation and the router-outlet renders nothing.
		try {
			const origin = this.getOrigin();
			if (origin) {
				this.setCanonicalUrl(origin + state.url);
			}
		} catch {
			// Canonical is best-effort; never block navigation.
		}
	}

	private getOrigin(): string {
		if (isPlatformBrowser(this.platformId)) {
			return this.doc.location.origin;
		}
		// Server-side: derive the origin from the incoming request so the
		// canonical is correct per environment (staging vs live) without
		// hardcoding a hostname.
		if (this.request) {
			const headers = this.request.headers || {};
			const proto = headers['x-forwarded-proto'] || this.request.protocol || 'https';
			const host = headers['x-forwarded-host'] || headers.host;
			if (host) {
				return `${proto}://${host}`;
			}
		}
		return '';
	}

	private setCanonicalUrl(url: string): void {
		const head = this.doc.head;
		if (!head) {
			return;
		}
		let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
		if (!link) {
			link = this.doc.createElement('link');
			link.setAttribute('rel', 'canonical');
			head.appendChild(link);
		}
		link.setAttribute('href', url);
	}
}
